import 'instrumentation-client';
import React, { useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import { ThemeProvider as Provider } from '@emotion/react';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import theme from 'theme';
import 'public/css/public.css';
import 'public/css/icon.css';
import 'react-toastify/dist/ReactToastify.css';
import MuiTheme from 'theme/MuiTheme';
import AuthProvider from 'components/AuthProvider';
import request, { requestMonitor } from 'services/request';
import ErrorBoundary from 'components/ErrorBoundry';
import ScrollToTopButton from 'components/Common/ScrollToTop';
import RotateModal from 'components/Common/RotateModal';
import UpdatePopup from 'components/Common/UpdatePopup';
import useUpdateAvailable from 'hooks/useUpdateAvailable';
import useAccountStore from 'store/account';
import { isIosPwa } from 'utils/common.util';
import PermissionActionSheet from 'components/Common/PermissionActionSheet';
import useProfileStore from 'store/profile';
import ExpireActionSheet from 'components/Common/ExpireActionSheet';

const PushNotificationLayout = dynamic(
  () => import('components/PushNotificationLayout'),
  { ssr: false },
);

const App: React.FC<AppProps> = ({ Component, pageProps }) => {
  const router = useRouter();
  const { isUpdateAvailable, setIsUpdateAvailable } = useUpdateAvailable();

  const setLastVisibilityStatus = useAccountStore(
    (state) => state.setLastVisibilityStatusAction,
  );

  const lastVisibilityStatus = useAccountStore(
    (state) => state.lastVisibilityStatus,
  );
  const permissionStatus = useProfileStore((state) => state.permissionStatus);

  const reloadApp = (): void => {
    if (
      lastVisibilityStatus === 'changed' &&
      document.visibilityState === 'visible'
    ) {
      setLastVisibilityStatus('');
      window.location.reload();
    }
  };

  useEffect(() => {
    // reload app to avoid changes in styles and fetch new data when open external link in iphone devices
    document.addEventListener('visibilitychange', reloadApp);
    return (): void =>
      document.removeEventListener('visibilitychange', reloadApp);
  }, [lastVisibilityStatus]);

  useEffect(() => {
    !request.monitors.length && request.addMonitor(requestMonitor);
    if (typeof window !== 'undefined') {
      // Identify the app running on pwa or twa and set flag in localStorage
      window.localStorage.setItem(
        'isTwa',
        String(document.referrer.includes('android-app')),
      );
    }
  }, []);

  useEffect(() => {
    // set minHeight to body because of ios PWA issue that reduce innerHeight
    // when user returns back from external link
    if (isIosPwa()) {
      const appHeight = window.localStorage.getItem('appHeight');
      if (appHeight) {
        document.getElementsByTagName('body')[0].style.minHeight = appHeight;
        window.localStorage.removeItem('appHeight');
      } else
        window.localStorage.setItem('appHeight', `${window.innerHeight}px`);
    }
  }, []);

  return (
    <>
      <Head>
        <title>Enigma app</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=no"
        />
      </Head>

      <ThemeProvider theme={createTheme(MuiTheme)}>
        <Provider theme={theme}>
          <AuthProvider>
            <PushNotificationLayout>
              <ErrorBoundary location={router.route}>
                <>
                  {isUpdateAvailable && (
                    <UpdatePopup
                      isOpen={isUpdateAvailable}
                      setOpen={setIsUpdateAvailable}
                    />
                  )}
                  <Component {...pageProps} />
                  <ScrollToTopButton />
                  <RotateModal />
                  {permissionStatus && <PermissionActionSheet />}
                  <ExpireActionSheet />
                </>
              </ErrorBoundary>
            </PushNotificationLayout>
          </AuthProvider>
        </Provider>
      </ThemeProvider>
    </>
  );
};

export default App;
